import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import {
  flightSearchFormInputSchema,
  type FlightSearchFormInputValues,
} from "@/features/flights/utils/flightsValidation";
import { initialFlightSearchFormValues } from "@/features/flights/utils/initialFlightsValues";
import {
  getHotelSearchFormInputSchema,
  type HotelSearchFormInputValues,
} from "@/features/hotels/utils/hotelsValidation";
import { initialHotelSearchFormValues } from "@/features/hotels/utils/initialHotelsValues";
import { applicationSettingsQueryOptions } from "@/hooks/useApplicationSettings";
import { useValidatedLocalStorage } from "@/hooks/useValidatedLocalStorage";

export const HOTELS_SEARCH_DATA_STORAGE_KEY = "tz_previousHotelSearchData@2";
export const FLIGHTS_SEARCH_DATA_STORAGE_KEY = "tz_previousFlightSearchData@2";

const createTravelDates = (startDate: Date, endDate?: Date) => {
  // if dates are in the past, create new ones based on travel duration
  if (dayjs(startDate).isBefore(new Date(), "day")) {
    const duration = endDate ? dayjs(endDate).diff(startDate, "day") || 1 : 1;
    return [
      dayjs().add(1, "day").hour(12).toDate(),
      dayjs()
        .add(duration + 1, "day")
        .hour(12)
        .toDate(),
    ];
  }

  // if dates are valid, use them
  return [startDate, endDate || startDate];
};

const createHotelTravelDates = (startDate: Date, endDate: Date) =>
  createTravelDates(startDate, endDate);
const createFlightTravelDates = (startDate: Date, endDate?: Date) =>
  createTravelDates(startDate, endDate);

export const usePreviousHotelSearchData = () => {
  const intl = useIntl();

  const { data: defaultDestination } = useQuery({
    ...applicationSettingsQueryOptions(),
    select: (settings) => settings.hotelSearch.defaultDestination,
  });

  const initialValues = useMemo(() => {
    return {
      ...initialHotelSearchFormValues,
      destination: defaultDestination || null,
    };
  }, [defaultDestination]);

  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<HotelSearchFormInputValues>(
      HOTELS_SEARCH_DATA_STORAGE_KEY,
      initialValues,
      getHotelSearchFormInputSchema(intl),
    );

  const isMultiRoomBookingEnabled = useFeatureIsOn("multi-room-booking");

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialValues;

    const [checkIn, checkOut] = createHotelTravelDates(
      searchData.dates.checkIn,
      searchData.dates.checkOut,
    );

    return {
      ...searchData,
      dates: {
        checkIn,
        checkOut,
      },
      rooms: isMultiRoomBookingEnabled
        ? searchData.rooms
        : [searchData.rooms[0]],
    };
  }, [initialValues, previousSearchData, isMultiRoomBookingEnabled]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};

export const usePreviousFlightSearchData = () => {
  const { data: defaultDeparture } = useQuery({
    ...applicationSettingsQueryOptions(),
    select: (settings) => settings.flightSearch.defaultOrigin,
  });

  const initialValues = useMemo(() => {
    return { ...initialFlightSearchFormValues, from: defaultDeparture || null };
  }, [defaultDeparture]);

  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<FlightSearchFormInputValues>(
      FLIGHTS_SEARCH_DATA_STORAGE_KEY,
      initialValues,
      flightSearchFormInputSchema,
    );

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialValues;

    const [departure, returnDate] = createFlightTravelDates(
      searchData.dates.departure,
      searchData.dates.return,
    );

    return {
      ...searchData,
      dates: {
        departure,
        return: returnDate,
      },
    };
  }, [initialValues, previousSearchData]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};
